import {
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
  forwardRef,
  ReactNode,
  type MouseEvent,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Button — control de acciones del sistema (polimorfico).
 *
 * Variantes visuales:
 *  - `primary`     : violeta de marca, accion principal.
 *  - `secondary`   : turquesa, accion secundaria.
 *  - `ghost`       : sin fondo, hover suave (uso en tablas / toolbars).
 *  - `outline`     : borde violeta, fondo transparente.
 *  - `danger`      : rojo error, acciones destructivas confirmadas.
 *
 * Renderiza segun props (discriminated union, sin `as`):
 *  - Si llega `to`        -> <Link to> de react-router (navegacion interna).
 *  - Si llega `href`      -> <a href> (links externos / mailto / tel / wa.me).
 *  - Sin `to` ni `href`   -> <button> (caso por defecto).
 *
 * `disabled` en variantes Link/anchor se simula via `aria-disabled` +
 * `tabIndex={-1}` + interceptacion de onClick. `loading` agrega `aria-busy`
 * y deshabilita interaccion en los 3 modos.
 *
 * Estados delegados a `disabled` y `loading`. El foco visible se hereda del
 * ring dorado global definido en `index.css` (`:focus-visible`).
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface BaseButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children: ReactNode;
  className?: string;
  /**
   * En modo button: deshabilita nativamente.
   * En modo Link/anchor: simula con aria-disabled + tabIndex={-1} + preventDefault.
   */
  disabled?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-controls'?: string;
  'aria-expanded'?: boolean;
  'aria-haspopup'?: boolean | 'dialog' | 'menu' | 'listbox' | 'tree' | 'grid';
  id?: string;
}

/** Variante Link (react-router). Se activa pasando `to`. */
export interface ButtonAsLinkProps extends BaseButtonProps {
  to: LinkProps['to'];
  replace?: boolean;
  state?: unknown;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  href?: never;
  type?: never;
  target?: never;
  rel?: never;
}

/** Variante anchor (links externos). Se activa pasando `href`. */
export interface ButtonAsAnchorProps extends BaseButtonProps {
  href: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  rel?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  to?: never;
  type?: never;
  replace?: never;
  state?: never;
}

/** Variante button (default). */
export interface ButtonAsButtonProps
  extends BaseButtonProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> {
  to?: never;
  href?: never;
  target?: never;
  rel?: never;
}

export type ButtonProps = ButtonAsLinkProps | ButtonAsAnchorProps | ButtonAsButtonProps;

// Mapa de variantes -> clases Tailwind sustentadas en tokens.
const variantClasses: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-primary-700 text-cream-50',
    'hover:bg-primary-800',
    'active:scale-[0.98]',
    'disabled:bg-primary-700 disabled:hover:bg-primary-700',
  ),
  secondary: cn(
    'bg-teal-700 text-cream-50',
    'hover:bg-teal-500',
    'active:scale-[0.98]',
  ),
  ghost: cn(
    'bg-transparent text-primary-900',
    'hover:bg-cream-200',
    'active:scale-[0.98]',
  ),
  outline: cn(
    'bg-transparent text-primary-700 border border-primary-700',
    'hover:bg-primary-700 hover:text-cream-50',
    'active:scale-[0.98]',
  ),
  danger: cn(
    'bg-error text-cream-50',
    'hover:opacity-90',
    'active:scale-[0.98]',
  ),
};

// Mapa de tamano -> altura, padding horizontal y radio.
const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-button rounded',
  md: 'h-10 px-4 text-button rounded-md',
  lg: 'h-12 px-6 text-button rounded-md',
};

// Tamano de iconos por talla para alineacion optica.
const iconSizeClasses: Record<ButtonSize, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

// Clases base compartidas por los 3 modos.
const baseClasses = cn(
  'inline-flex items-center justify-center gap-2',
  'font-body uppercase tracking-[0.04em]',
  'transition-colors duration-2 ease-out',
  'select-none',
);

// Type guards para el discriminated union.
function isLinkProps(props: ButtonProps): props is ButtonAsLinkProps {
  return 'to' in props && props.to !== undefined;
}
function isAnchorProps(props: ButtonProps): props is ButtonAsAnchorProps {
  return 'href' in props && props.href !== undefined;
}

/**
 * Contenido interno compartido entre los 3 modos (button | Link | anchor).
 */
interface InnerContentProps {
  loading: boolean;
  leftIcon: ReactNode;
  rightIcon: ReactNode;
  size: ButtonSize;
  children: ReactNode;
}
function InnerContent({ loading, leftIcon, rightIcon, size, children }: InnerContentProps) {
  return (
    <>
      {loading ? (
        <Loader2 className={cn('animate-spin', iconSizeClasses[size])} aria-hidden="true" />
      ) : leftIcon ? (
        <span className={cn('inline-flex shrink-0', iconSizeClasses[size])} aria-hidden="true">
          {leftIcon}
        </span>
      ) : null}
      <span className={cn(loading && 'opacity-70')}>{children}</span>
      {!loading && rightIcon ? (
        <span className={cn('inline-flex shrink-0', iconSizeClasses[size])} aria-hidden="true">
          {rightIcon}
        </span>
      ) : null}
    </>
  );
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (props, ref) => {
    const {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      className,
      children,
    } = props;

    const isInactive = Boolean(disabled || loading);

    const classNameFinal = cn(
      baseClasses,
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
      // Para Link/anchor en estado disabled/loading replicamos visual.
      isInactive && (isLinkProps(props) || isAnchorProps(props)) &&
        'opacity-50 cursor-not-allowed pointer-events-none active:scale-100',
      variantClasses[variant],
      sizeClasses[size],
      fullWidth && 'w-full',
      className,
    );

    const inner = (
      <InnerContent
        loading={loading}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        size={size}
        children={children}
      />
    );

    // --- Modo Link (react-router) ---
    if (isLinkProps(props)) {
      const { to, replace, state, onClick } = props;
      const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
        if (isInactive) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      };
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={to}
          replace={replace}
          state={state}
          onClick={handleClick}
          className={classNameFinal}
          aria-disabled={isInactive || undefined}
          aria-busy={loading || undefined}
          tabIndex={isInactive ? -1 : undefined}
          aria-label={props['aria-label']}
          aria-describedby={props['aria-describedby']}
          aria-controls={props['aria-controls']}
          aria-expanded={props['aria-expanded']}
          aria-haspopup={props['aria-haspopup']}
          id={props.id}
        >
          {inner}
        </Link>
      );
    }

    // --- Modo anchor (link externo / mailto / tel / wa.me) ---
    if (isAnchorProps(props)) {
      const { href, target, rel, onClick } = props;
      // Si target="_blank" pero no se pasa rel, garantizar noopener para seguridad.
      const safeRel = target === '_blank' ? rel ?? 'noopener noreferrer' : rel;
      const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
        if (isInactive) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      };
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={safeRel}
          onClick={handleClick}
          className={classNameFinal}
          aria-disabled={isInactive || undefined}
          aria-busy={loading || undefined}
          tabIndex={isInactive ? -1 : undefined}
          aria-label={props['aria-label']}
          aria-describedby={props['aria-describedby']}
          aria-controls={props['aria-controls']}
          aria-expanded={props['aria-expanded']}
          aria-haspopup={props['aria-haspopup']}
          id={props.id}
        >
          {inner}
        </a>
      );
    }

    // --- Modo button (default) ---
    // Extraer todo lo que NO va al DOM nativo y dejar `rest` con HTML attrs.
    const {
      variant: _v,
      size: _s,
      fullWidth: _fw,
      loading: _l,
      leftIcon: _li,
      rightIcon: _ri,
      disabled: _d,
      className: _cn,
      children: _c,
      // discriminator-only — nunca presentes en runtime para esta variante,
      // pero TS los ve en la union.
      to: _to,
      href: _href,
      target: _target,
      rel: _rel,
      type = 'button',
      ...rest
    } = props as ButtonAsButtonProps & {
      to?: never;
      href?: never;
      target?: never;
      rel?: never;
    };
    void _v;
    void _s;
    void _fw;
    void _l;
    void _li;
    void _ri;
    void _d;
    void _cn;
    void _c;
    void _to;
    void _href;
    void _target;
    void _rel;

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        disabled={isInactive}
        aria-disabled={isInactive || undefined}
        aria-busy={loading || undefined}
        className={cn(
          baseClasses,
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && 'w-full',
          className,
        )}
        {...rest}
      >
        {inner}
      </button>
    );
  },
);

Button.displayName = 'Button';
